import { memo } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";

import { useOnChangeAction } from "../../hooks";
import { RootState } from "../../store";
import { AutocompleteWithError } from "../Autocomplete";
import { CheckboxWithError } from "../Checkbox";
import { Copy, CopyProps } from "../Copy";
import { DatePickerWithError } from "../DatePicker";
import { InputWithError } from "../Input";
import { MarketingCheckboxWithError } from "../MarketingCheckbox";
import { PhoneWithError } from "../Phone";
import { RadioWithError } from "../Radio";
import { SelectWithError } from "../Select";
import { SelectCityWithError } from "../SelectCity";
import { SelectMakeWithError } from "../SelectMake/SelectMake";
import { SelectModelWithError } from "../SelectModel/SelectModel";
import { TextareaWithError } from "../Textarea";
import { TimePickerWithError } from "../TimePicker";
import { ZipCodeWithError } from "../ZipCode";
import { FormElement } from "../enums";
import { SimpleFormSchemaComponents } from "../types";

type SimpleComponentsFactoryProps = {
  readonly component: SimpleFormSchemaComponents;
};

export const SimpleComponentFactory = memo<SimpleComponentsFactoryProps>(
  ({ component }) => {
    const {
      control,
      errors,
      formState: { isSubmitted },
      register,
      setValue,
      trigger,
    } = useFormContext();
    const {
      type,
      disabled: disabledSelector,
      onChange,
      required: requiredSelector,
      ...restComponentProps
    } = component;
    const isDisabled = useSelector((state: RootState) => {
      if (typeof disabledSelector === "boolean" || !disabledSelector) {
        return false;
      }

      return disabledSelector(state);
    });
    const isRequired = useSelector((state: RootState) => {
      if (typeof requiredSelector === "boolean" || !requiredSelector) {
        return requiredSelector;
      }

      return requiredSelector(state);
    });
    const onChangeAction = useOnChangeAction(onChange);
    const componentProps = {
      ...restComponentProps,
      errors,
      disabled: isDisabled,
      onChange: onChangeAction,
      required: isRequired,
    };

    switch (type) {
      case FormElement.AUTOCOMPLETE:
        return <AutocompleteWithError {...componentProps} control={control} />;

      case FormElement.CHECKBOX:
        return <CheckboxWithError {...componentProps} register={register} />;

      case FormElement.MARKETING_CHECKBOX:
        return (
          <MarketingCheckboxWithError {...componentProps} register={register} />
        );

      case FormElement.DATE_PICKER:
        return (
          <DatePickerWithError
            {...componentProps}
            control={control}
            isSubmitted={isSubmitted}
          />
        );

      case FormElement.INPUT:
        return (
          <InputWithError
            {...componentProps}
            control={control}
            register={register}
            setValue={setValue}
          />
        );

      case FormElement.ZIP_CODE:
        return (
          <ZipCodeWithError
            {...componentProps}
            control={control}
            register={register}
            setValue={setValue}
            trigger={trigger}
          />
        );

      case FormElement.PHONE:
        return (
          <PhoneWithError
            {...componentProps}
            control={control}
            setValue={setValue}
            trigger={trigger}
          />
        );

      case FormElement.NUMBER:
        return (
          <InputWithError
            isNumber
            {...componentProps}
            register={register}
            control={control}
            setValue={setValue}
          />
        );

      case FormElement.RADIO:
        return <RadioWithError {...componentProps} register={register} />;

      case FormElement.SELECT:
        return (
          <SelectWithError
            {...componentProps}
            control={control}
            trigger={trigger}
          />
        );

      case FormElement.SELECT_CITY:
        return (
          <SelectCityWithError
            {...componentProps}
            control={control}
            trigger={trigger}
          />
        );

      case FormElement.SELECT_MAKE:
        return (
          <SelectMakeWithError
            {...componentProps}
            control={control}
            trigger={trigger}
          />
        );

      case FormElement.SELECT_MODEL:
        return (
          <SelectModelWithError
            {...componentProps}
            control={control}
            trigger={trigger}
          />
        );

      case FormElement.TEXTAREA:
        return <TextareaWithError {...componentProps} register={register} />;

      case FormElement.TIME_PICKER:
        return <TimePickerWithError {...componentProps} register={register} />;

      case FormElement.COPY:
        return <Copy {...(componentProps as CopyProps)} />;

      default:
        throw new Error(
          "You have to pass proper component into form component factory"
        );
    }
  }
);
